import { trigger, state, style, transition, animate, group } from '@angular/animations';
import { Component, Input, OnInit, ViewChild, ChangeDetectorRef, NgZone } from '@angular/core';
import { DatePipe } from '@angular/common';
import { BehaviorSubject, Subscription } from 'rxjs';
import { BaseComponent } from '../../base/base.component';
import { ErrorReportingService } from '../../services/errorReporting/error-reporting.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BusyIndicatorComponent } from 'src/app/views/shared/busy-indicator/busy-indicator.component';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { StepModel } from 'src/app/views/dataTransformConfig/ingest/ingest-work-flow-layout/ingest-work-flow-layout.component';
import { ActivatedRoute, Router } from '@angular/router';
import { NavigationService } from 'src/app/services/navigation/navigation.service';
import { ConfigViewModelService } from 'src/app/services/configViewModel/config-view-model.service';
import { CacheKeyType, TransformType, TransformTypeUtil } from 'src/app/domainObjects/transformType';
import { DataTransform } from 'src/app/domainObjects/dataTransform';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { DataFarmType, DataFarmTypeUtil } from 'src/app/domainObjects/dataFarmType';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { RowExtractorTransform } from 'src/app/domainObjects/rowExtractorTransform';
import { ConfigService } from '../../services/config/config.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EventNamerTransform } from 'src/app/domainObjects/transformers/eventNamerTransform';
import { EventGroupNamerTransform } from 'src/app/domainObjects/transformers/eventGroupNamerTransform';
import { GroupAttributorTransform } from 'src/app/domainObjects/transformers/groupAttributorTransform';
import { ColumnCalcTransform } from 'src/app/domainObjects/transformers/columnCalcTransform';
import { ActivityNamingTransform } from 'src/app/domainObjects/transformers/activityNamingTransform';
import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDragHandle} from '@angular/cdk/drag-drop';
import { IngestConfigViewModel } from '../import-data/import-data.component';
import { ColumnLookupTransform } from 'src/app/domainObjects/transformers/columnLookupTransform';
import { UserDataService } from 'src/app/services/user-data-service';
import { FarmConverterTransform } from 'src/app/domainObjects/transformers/farmConverterTransform';
import { FarmType, RestrictTransformers, Transformers } from 'src/app/domainObjects/masterConfig';
import { ConfigDetailComponent, ConfigDetailViewModel } from 'src/app/views/config/config-detail/config-detail.component';

import { DataTransformerConfig } from 'src/app/domainObjects/dataTransformConfig';
import { DataConnectorMapping } from 'src/app/domainObjects/dataConnectorMapping';
import { NodeRunStatus } from 'src/app/domainObjects/nodeRunStatus';
import { ResetTransformerRequest, RunrefineryRequest, UpdateNodesStatusRequest } from 'src/app/model/RunrefineryRequest';
import { JoinerTransform } from 'src/app/domainObjects/transformers/joinerTransform';
import { RowFillerTransform } from 'src/app/domainObjects/transformers/rowFillerTransform';
import { AnonymizerTransform } from 'src/app/domainObjects/transformers/anonymizerTransform';
import { ColumnCleanerTransform } from 'src/app/domainObjects/transformers/columnCleanerTransform';
import { DataDictionaryType } from 'src/app/domainObjects/dataDictionaryType';
import { CacheStatusRequest, ResetNodeStatusRequest } from 'src/app/messaging/resetRefineryNodesStatusRequest';
import { SharedService } from 'src/app/services/shared.service';

export class TransformListViewModel {
  transforms: TransformerViewModel[] = [];

  constructor(dataTransforms?: DataTransform<any>[]) {

    if (dataTransforms?.length > 0) {
      this.transforms = dataTransforms.map( item => {
        return new TransformerViewModel(item);
      }).sort((a, b) => a.index - b.index);
    }
  }

  canAddTransformerToFarm(
    ingestFarms: IngestConfigViewModel[],
    farmType: DataFarmType): boolean {
    return ingestFarms.filter( i => i.targetDataFarm === farmType).length > 0;
  }

  syncTransforms(list: TransformerViewModel[]): void {
    for ( const t of this.transforms) {
      const transformer = list.find( i => i.guid === t.guid);
      if (transformer) {
        transformer.transform.index = t.index;
        transformer.index = t.index;
        // this.transforms (the table) and dataTransformConfigViewModel.transforms (what
        // saveConfigImpl() actually serializes) are separate parallel wrapper arrays, not
        // shared DataTransform references — the Run toggle must propagate here the same
        // way index already does, or it never reaches the save payload. Confirmed
        // 2026-09-26 (the Architect): a toggle-off round-tripped to disk still showing
        // run=true because this method predates that field and only ever synced index.
        transformer.transform.run = t.transform.run;
      }
    }
  }

  reindexTransforms(): void {
    for (let i = 0; i < this.transforms.length; i++) {
      this.transforms[i].index = i + 1;
      this.transforms[i].transform.index = i + 1;
    }
  }
}

export class TransformerViewModel {
  guid?: string;
  id?: number;
  index: number;
  name: string;
  description: string;
  type: TransformType;
  typeDisplayName: string;
  isEnabled = true;
  transform: DataTransform<any>;
  nodeID?: string;
  transformConfig?: any;
  isCached?: string;
  /** Last Run Node result (Completed/Failed/Not Run) — populated by updateTransformerIsCached(). */
  lastRunStatus?: string;
  lastRunMessage?: string;
  /** Last Run Node elapsed time for this transformer, "N.nn secs" — blank when this
   *  transformer didn't run (disabled or skipped) in the last Run Node/Processor/Refinery
   *  invocation. Populated by updateTransformerIsCached(). */
  lastRunTime?: string;
  /** Last Run Node output row count for this transformer — null (blank cell, formatted
   *  with the `number` pipe) when it produced zero rows (e.g. skipped/"Not Run").
   *  Populated by updateTransformerIsCached(). */
  lastRunOutputRows?: number | null;
  /** Last Run Node output column count for this transformer — null (blank cell)
   *  whenever lastRunOutputRows is also null, same convention, same write site.
   *  Populated by updateTransformerIsCached(). */
  lastRunOutputCols?: number | null;

  constructor(transform: DataTransform<any>) {
    this.guid = transform.guid;
    this.id = transform.id;
    this.transform = transform;
    this.index = transform.index;
    this.name = transform.name;
    this.description = transform.description;
    this.type = transform.transformType;
    this.typeDisplayName = TransformTypeUtil.toString(transform.transformType);
    this.nodeID=transform.nodeID;
  }
}

@Component({
    selector: 'app-transformer-list',
    templateUrl: './transformer-list.component.html',
    styleUrls: ['./transformer-list.component.css'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({ height: '0px', minHeight: '0', display: 'none' })),
            state('expanded', style({ height: '*' })),
            transition('expanded <=> collapsed', animate('0ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    standalone: false
})
export class TransformerListComponent extends BaseComponent implements OnInit {
  @Input() dataTransformConfigViewModel: DataTransformConfigViewModel;
  @Input() stepModel: StepModel;
  @Input() viewModel: TransformListViewModel;
  @Input() dataFarmType: DataFarmType;
  @Input() dataFarmName: string;
  @ViewChild('table') table: MatTable<MatTableDataSource<TransformerViewModel>>;

  TransformerType = TransformType;
  TransformTypeUtil = TransformTypeUtil;
  configviewModel: ConfigDetailViewModel;
  // Saves of this list run one at a time, in order. A transformer page resolves its input
  // from the SAVED config (server-side), so a transformer must never be opened before the
  // save that contains it has landed -- opening one within ~1s of Add failed with
  // "Transformer ... not found in Node ...'s saved config" (RCA 2026-10-07).
  private saveChain: Promise<boolean> = Promise.resolve(true);
  // Transformers added in this list whose save has not succeeded yet.
  private unsavedTransformGuids = new Set<string>();
  // configComponent = new ConfigDetailComponent()

  columnsToDisplay = ['dragHandle', 'index', 'name', 'description', 'laststatus', 'outputrows', 'outputcols', 'runtime', 'iscached', 'runToggle','actions'];
  dataSourceBehaviorSubject = new BehaviorSubject<TransformerViewModel[]>(null);
  dataSource = new MatTableDataSource<TransformerViewModel>();
  expandedTransformer: TransformerViewModel;
  private subscription!: Subscription;
  cacheStatus:string="";
  
  constructor(
    errorLogger: ErrorReportingService,
    matDialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private configService: ConfigService,
    private navigationService: NavigationService,
    private configViewModelService: ConfigViewModelService,
    private snackBar: MatSnackBar,
    private datePipe: DatePipe,
    private userData: UserDataService,
    private sharedService: SharedService,
    private cdr: ChangeDetectorRef,
    private zone: NgZone) {
    super(errorLogger, matDialog);
    
  }

  async ngOnInit(): Promise<void> {
    super.ngOnInit();
    const node = this.userData.getSelectedNodeDetails();

    // Emit initial data
    this.dataSourceBehaviorSubject.next(this.viewModel.transforms);

    // Subscribe
    this.dataSourceBehaviorSubject.subscribe((data: TransformerViewModel[]) => {
      if (data) {
        this.dataSource.data = data.filter(x => x.nodeID === node.id);
        this.cdr.detectChanges();
      }
    });

    // Run async update safely
    Promise.resolve().then(async () => {
      await this.updateTransformerIsCached();
      this.zone.run(() => {
        this.refreshLayout();
        this.cdr.detectChanges();
      });
    });

    // Restrict Transformers
    await this.RestrictTransformers();

    // Refresh preview
    this.subscription = this.sharedService.getEvent().subscribe(async event => {
      if (event === "display_output_preview") {
        setTimeout(async () => {
          await this.updateTransformerIsCached();
          this.zone.run(() => {
          this.refreshLayout();
          this.cdr.detectChanges();
        });
        }, 1000);
      }else if (event === "reset_node_successfully") {
        setTimeout(async () => {
          await this.updateTransformerIsCached();
          this.zone.run(() => {
          this.refreshLayout();
          this.cdr.detectChanges();
        });
        }, 1000);
      }
    });
  }

  
  ngOnDestroy() {
    if(this.subscription){
      this.subscription.unsubscribe(); // Clean up to avoid memory leaks
    }
  }

  restricttransformers: RestrictTransformers[];
  async RestrictTransformers(): Promise<void> {
    var node=this.userData.getSelectedNodeDetails();
    var masterconfig = this.userData.getMasterFarmtypes();//await this.configService.getMasterConfigs();
    this.restricttransformers=masterconfig.find(x=>x.id==node.farmtypeid).restricttransformers;
  }
  restrictTransformers(transformertype:number){
    if(this.restricttransformers){
      const transformer = this.restricttransformers.find(x => x.transformerid === transformertype);
      return transformer ? transformer.allowed : false;
    }
    return true;
  }
  async ngAfterViewInit(): Promise<void> {
    var node=this.userData.getSelectedNodeDetails();
    if(node.nodeboxtypeid==4){
      if(!(this.viewModel.transforms.find(x=>x.nodeID=== node.id))){
        if (!(await this.addTransformer(this.TransformerType.farmConverter))) { return; }
      }
      var transform=this.viewModel.transforms.find(x=>x.nodeID=== node.id);
        let configId = this.dataTransformConfigViewModel.dataTransformerConfig?.guid;
        if (!configId) {
          configId = null;
        }

        // Notify active transform changed
        this.dataTransformConfigViewModel.activeTransformGuid = transform.guid;
        this.configViewModelService.setDataTransformConfigViewModel(this.dataTransformConfigViewModel);

        //FOR dtore in storage further use/ remove state/ reload page avoid error
        this.userData.setSelectedTransform(transform);
        this.userData.setTransformId(transform.guid);
        this.userData.setSelectedDataTransformConfigViewModel(this.dataTransformConfigViewModel);

        this.router.navigateByUrl(`dataTransformConfig/${configId}/farmConverterTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
    }else if(node.nodeboxtypeid==3){
      if(!(this.viewModel.transforms.find(x=>x.nodeID=== node.id))){
        if (!(await this.addTransformer(this.TransformerType.farmJoiner))) { return; }
      }
      var transform=this.viewModel.transforms.find(x=>x.nodeID=== node.id);
        let configId = this.dataTransformConfigViewModel.dataTransformerConfig?.guid;
        if (!configId) {
          configId = null;
        }

        // Notify active transform changed
        this.dataTransformConfigViewModel.activeTransformGuid = transform.guid;
        this.configViewModelService.setDataTransformConfigViewModel(this.dataTransformConfigViewModel);

        //FOR dtore in storage further use/ remove state/ reload page avoid error
        this.userData.setSelectedTransform(transform);
        this.userData.setTransformId(transform.guid);
        this.userData.setSelectedDataTransformConfigViewModel(this.dataTransformConfigViewModel);
        
        this.router.navigateByUrl(`dataTransformConfig/${configId}/joinerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
    }
  }

  async dropTable(event: CdkDragDrop<MatTableDataSource<TransformerViewModel>>): Promise<void> {
    const busyDialog = this.showBusyDialog('Reordering & Reseting transforms...');
    try {
      var currentIndex = event.currentIndex+1;
      var previousIndex = event.previousIndex+1;
      var fromResetIndex=0;
      if(currentIndex != previousIndex){
        var nodeID=this.userData.getSelectedNodeDetails().id;
        const prevItem = this.viewModel.transforms.filter(x=>x.nodeID === nodeID)[event.previousIndex];
        if(currentIndex < previousIndex){
          fromResetIndex=currentIndex;
          var transforms=this.viewModel.transforms.filter(x=>x.nodeID === nodeID && (x.index>=currentIndex && x.index<previousIndex));
          transforms.forEach(transform=>{
            transform.index=transform.index+1;
          })
        }else{
          fromResetIndex=previousIndex;
          var transforms=this.viewModel.transforms.filter(x=>x.nodeID === nodeID && (x.index<=currentIndex && x.index>previousIndex));
          transforms.forEach(transform=>{
            transform.index=transform.index-1;
          })
        }
        prevItem.index = currentIndex;

        this.viewModel.transforms.filter(x=>x.nodeID === nodeID && x.index>=fromResetIndex).forEach(trans=>{
          trans.isCached="Not Cached";
        });
        this.refreshLayout();
        // notify change
        this.viewModel.syncTransforms(this.dataTransformConfigViewModel.transforms);
        this.configViewModelService.setDataTransformConfigViewModel(this.dataTransformConfigViewModel);
        const currenttransformer=this.viewModel.transforms.find(trans=>trans.nodeID===this.userData.getNodeId() && trans.index==fromResetIndex);
        await this.dropTableAsync(currenttransformer.guid);
      }
      
    } catch (err) {
      this.displayError(`Failed to Reordering & Reseting transforms`, 'Error', err);
    } finally {      
      busyDialog.close();
    }
  }
  async dropTableAsync(transformerGuid: string): Promise<void> {
    await this.saveConfigImpl(history.state.nodename);
    await this.resetTransformer(transformerGuid);
    setTimeout(async () => {
      await this.updateTransformerIsCached();
      this.refreshLayout();
      this.sharedService.sendEvent("get_node_status_update");
    }, 1000);
  }

  /** Drives the header toggle's [checked] binding -- previously unbound, so it never
   *  reflected the row toggles at all (the Architect, 2026-09-27: toggling every row
   *  on/off through the header didn't leave the header itself showing On/Off to
   *  match). True only when every transformer in this node has Run on; false if any
   *  is off, including a mixed state -- matching a master toggle's usual meaning of
   *  "are they ALL on." */
  get allNodeTransformersRunEnabled(): boolean {
    const nodeID = this.userData.getSelectedNodeDetails().id;
    const nodeTransforms = this.viewModel.transforms.filter(x => x.nodeID === nodeID);
    return nodeTransforms.length > 0 && nodeTransforms.every(t => t.transform.run);
  }

  /** Node-page master "Run?" toggle — sets every transformer in this node at once,
   *  mirroring onRunChanged() in each transform type's own rules table. */
  onRunChanged(event: MatSlideToggleChange): void {
    const nodeID = this.userData.getSelectedNodeDetails().id;
    this.viewModel.transforms.filter(x => x.nodeID === nodeID).forEach(t => t.transform.run = event.checked);
    const firstTransformer = this.viewModel.transforms
      .filter(x => x.nodeID === nodeID)
      .sort((a, b) => a.index - b.index)[0];
    this.saveRunToggle(firstTransformer?.guid);
  }

  /** Per-row "Run?" toggle change — the Architect, 2026-09-26: false skips this
   *  transformer's execution entirely and passes its input straight through unchanged. */
  onTransformerRunToggled(transformer: TransformerViewModel): void {
    this.saveRunToggle(transformer.guid);
  }

  private async saveRunToggle(transformerGuid?: string): Promise<void> {
    // Delayed-show: the save chain now takes ~1s (RCA'd and fixed 2026-09-26), so the
    // dialog only appears if it runs past a short threshold, instead of flashing open
    // for a near-instant operation. disableClose still guards against a second toggle
    // racing this one, whether or not the dialog ends up visible. The Architect,
    // 2026-09-26.
    let busyDialog: MatDialogRef<BusyIndicatorComponent> | null = null;
    const busyDialogTimer = setTimeout(() => {
      busyDialog = this.showBusyDialog('Reconfiguring Node');
    }, 300);
    try {
      // Optimistic UI update -- scoped to the toggled transformer and everything
      // downstream of it (by index), matching exactly what the server-side reset
      // (updateDownstreamStatus, isupdateSelf=True) actually invalidates. Previously
      // this marked EVERY transformer in the node "Not Cached", including upstream
      // ones the reset never touches -- their real (still-cached) state then came
      // back moments later from updateTransformerIsCached() below and flipped them
      // back to "Cached", a visible flicker the Architect noticed 2026-09-26. The
      // toggle-all header control still marks the whole node, since it passes the
      // first transformer's guid, whose index is the minimum -- every transformer's
      // index is ">=" it.
      const nodeID = this.userData.getSelectedNodeDetails().id;
      const toggledTransformer = transformerGuid
        ? this.viewModel.transforms.find(x => x.guid === transformerGuid)
        : undefined;
      this.viewModel.transforms
        .filter(x => x.nodeID === nodeID && (!toggledTransformer || x.index >= toggledTransformer.index))
        .forEach(trans => {
          trans.isCached = "Not Cached";
        });
      // Propagate the toggled run flag from the table's own wrapper array into the
      // array saveConfigImpl() actually serializes — same call dropTable (reorder)
      // already makes for its own index change.
      this.viewModel.syncTransforms(this.dataTransformConfigViewModel.transforms);
      await this.saveConfigImpl(history.state.nodename);
      // The config hash used for cache validity is computed only from transformConfig,
      // not this outer run flag, so a toggle alone wouldn't invalidate a stale cached
      // output on its own — explicitly reset, same mechanism reorder/delete already use.
      // resetTransformer() -> POST /api/v1.0/engine/resetTransformerCache ->
      // updateDownstreamStatus(..., NodeRunStatusType.PipelineModified, isupdateSelf=True),
      // identical to dropTableAsync's own call — sets this node's status to Pipeline
      // Modified and cascades to every downstream node, same as a reorder. The Architect,
      // 2026-09-26.
      if (transformerGuid) {
        await this.resetTransformer(transformerGuid);
      }
      // resetTransformer's POST only resolves once the server-side cache invalidation
      // is fully committed, so the status poll can run immediately -- no artificial
      // wait needed. The flat 1000ms delay that used to sit here was sized for when
      // the underlying save chain itself took several seconds (RCA'd and fixed
      // 2026-09-26: redundant refinery-config writes, a per-node cache-reset loop, and
      // a stale compiled-config hash forcing a needless recompile on every save); with
      // those fixed, polling right away is correct, not premature. The Architect,
      // 2026-09-26.
      await this.updateTransformerIsCached();
      this.refreshLayout();
      this.sharedService.sendEvent("get_node_status_update");
    } catch (err) {
      this.displayError('Failed to save the Run toggle', 'Error', err);
    } finally {
      clearTimeout(busyDialogTimer);
      busyDialog?.close();
    }
  }
  async ngOnChanges(changes: any): Promise<void> {
    //console.log(`TransformList::onChanges`);
    ////console.log(changes);

    if (changes.viewModel.firstChange === true || !changes.viewModel.previousValue) {
      return;
    }

    if (changes.viewModel.previousValue === changes.viewModel.currentValue) {
      return;
    }

    if (changes.viewModel.previousValue !== changes.viewModel.currentValue) {
      // await this.refreshLayout();
    }
  }

  async refreshLayout(): Promise<void> {
    ////console.log('TransformList::refreshing layout');
    this.viewModel.transforms=this.viewModel.transforms.sort((a, b) => a.index - b.index);
    this.dataSourceBehaviorSubject.next(this.viewModel.transforms);
    //console.log("this.viewModel.transforms ",this.viewModel.transforms);
    //console.log("this.dataTransformConfigViewModel.transforms ",this.dataTransformConfigViewModel.transforms);
  }

  deleteTransformer(item: TransformerViewModel): void {
    this.showConfirmDialog('Delete Transform', `Are you sure you want to delete ${item.name}?`, async (confirmed) => {
      if (confirmed) {
        const busyDialog = this.showBusyDialog('Deleting transform...');
        try {
          var nodeID=this.userData.getSelectedNodeDetails().id;
          const items = this.viewModel.transforms;
          const index = items.indexOf(item, 0);
          if (index > -1) {
            items.splice(index, 1);
            let j = 1;
            items.filter(tr=>tr.nodeID==nodeID).forEach(i => {
              i.index = j++;
            });
            // Remove from config Transformers also
            const existingTransformer = this.dataTransformConfigViewModel.transforms.find(t => t.guid === item.guid);
            const transformersIndex = this.dataTransformConfigViewModel.transforms.indexOf(existingTransformer, 0);
            if (transformersIndex > -1) {
              this.dataTransformConfigViewModel.transforms.splice(transformersIndex, 1);
              let k = 1;
              this.dataTransformConfigViewModel.transforms.filter(tr=>tr.nodeID==nodeID).forEach(i => {
                i.index = k++;
              });
            }
            this.viewModel.syncTransforms(this.dataTransformConfigViewModel.transforms);
            // notify change
            this.configViewModelService.setDataTransformConfigViewModel(this.dataTransformConfigViewModel);
            await this.resetTransformer(item.guid);
            await this.saveConfigImpl(history.state.nodename);
            this.snackBar.open(`Successfully deleted transform`, 'Dismiss', {
              duration: 5000
            });
            setTimeout(async () => {
              await this.updateTransformerIsCached();
              this.sharedService.sendEvent("get_node_status_update");
              await this.refreshLayout();
              busyDialog.close();
            }, 1000);
          }
          
        } catch (e) {
          busyDialog.close();
          this.displayError('Failed to delete transform', 'Error', e);
        }finally{

        }
      }
    });
  }

  async showTransformDetail(transform: TransformerViewModel): Promise<void> {
    await this.saveChain;
    if (this.unsavedTransformGuids.has(transform.guid)) {
      this.displayWarning('This transformer has not been saved yet, so it cannot be opened. Please try adding it again.',
        'Transformer not saved');
      return;
    }
    // this.navigationService.openSideDrawer();
    // this.router.navigate(['./', { outlets: {sidebar: 'extractorTransformDetail'}}]);

    // A disabled transformer's own detail page can't be opened at all (the Architect,
    // 2026-09-27): running/editing it there shares the same bypass-check code path as a
    // real node run, so opening it while off risks the Analyst believing they're testing
    // their actual rules when the transformer is really just passing input through
    // unchanged -- a conflicting condition the Architect wants ruled out entirely rather
    // than merely flagged. Re-enable the Run toggle first to edit/open it.
    if (transform.transform.run === false) {
      this.displayWarning(
        'Disabled Transformers cannot be edited. Please enable this transformer if you would like to edit/open it.',
        'Transformer disabled at Node level'
      );
      return;
    }

    ////console.log(`show transform detail: ${transform.type}`);
    ////console.log(transform);

    let configId = this.dataTransformConfigViewModel.dataTransformerConfig?.guid;
    if (!configId) {
      configId = null;
    }

    // Notify active transform changed
    this.dataTransformConfigViewModel.activeTransformGuid = transform.guid;
    this.configViewModelService.setDataTransformConfigViewModel(this.dataTransformConfigViewModel);

    //FOR dtore in storage further use/ remove state/ reload page avoid error
    this.userData.setSelectedTransform(transform);
    this.userData.setTransformId(transform.guid);
    this.userData.setSelectedDataTransformConfigViewModel(this.dataTransformConfigViewModel);

    // const masterconfig=this.userData.getMasterTransformers().find(x=>x.id==transform.type);
    // if(!masterconfig){
    //   this.displayWarning("Please set transformer in the JSON configuration.","JSON configuration not set yet");
    //   return;
    // }
    // else{
    //   const selectedNode=this.userData.getSelectedNodeDetails();
    //   var farmtypesetting=masterconfig.parameters.find(x=>x.farmtypeid==selectedNode.farmtypeid);
    //   if(!farmtypesetting){
    //     this.displayWarning("Please set farm type in the JSON configuration.","JSON configuration not set yet");
    //     return;
    //   }
    //   else{
    //     if(!farmtypesetting.parameters){
    //       this.displayWarning("Please set parameters in the JSON configuration.","JSON configuration not set yet");
    //       return;
    //     }
    //   }
    // }

    switch (transform.type) {
      case TransformType.rowExtractor:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/extractorTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.activityNamer:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/activityNamingTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.eventGroupNamer:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/eventGroupNamerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.eventNamer:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/eventNamerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.groupAttributor:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/groupAttributorTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;

      case TransformType.columnCalc:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/columnCalcTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;
      case TransformType.columnLookup:
        var attribute=await this.countNodeAttributes();
        if(attribute>0){
          this.router.navigateByUrl(`dataTransformConfig/${configId}/columnLookupTransformDetail/${transform.guid}`,
            {state: {transformViewModel: transform}});
        }else{
          this.displayInfo("Cannot add a Column Lookup Transformer as there are no attribution farms connected to the Processor Node.   Please add an attribution farm from the Refinery Menu.","Info");
        }
        break;
      case TransformType.rowFiller:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/rowFillerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;
      case TransformType.anonymizer:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/anonymizerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;
      case TransformType.columnCleaner:
        this.router.navigateByUrl(`dataTransformConfig/${configId}/columnCleanerTransformDetail/${transform.guid}`,
          {state: {transformViewModel: transform}});
        break;
    }
  }

  getTransformerIcon(transformerType: TransformType): string {
    const basePath = 'assets/images';

    // transformer display name
    const transformerMap: { [key in TransformType]?: string } = {
      [TransformType.rowExtractor]: 'Row Extractor',
      [TransformType.activityNamer]: 'Activity Namer',
      [TransformType.eventGroupNamer]: 'Event Group Namer',
      [TransformType.eventNamer]: 'Event Namer',
      [TransformType.groupNamer]: 'Group Namer',
      [TransformType.groupAttributor]: 'Group Attributor',
      [TransformType.groupFiller]: 'Group Filler',
      [TransformType.columnCleaner]: 'Column Cleaner',
      [TransformType.columnCalc]: 'Column Calc',
      [TransformType.columnLookup]: 'Column Lookup',
      [TransformType.farmConverter]: 'Farm Converter',
      [TransformType.farmJoiner]: 'Farm Joiner',
      [TransformType.rowFiller]: 'Row Filler',
      [TransformType.anonymizer]: 'Anonymizer'
    };
    const transformerName = transformerMap[transformerType] || 'Default';

      
    //console.log('config details Node prajna = ',this.userData.getSelectedNodeDetails().farmtypeid);
    // take farm from targetDataFarm (fallback to this.dataFarmType if not present)
  // const targetDataFarm = this.dataTransformConfigViewModel.ingestConfigs?.[0]?.targetDataFarm;
  const targetDataFarm = this.userData.getSelectedNodeDetails().farmtypeid;
    let fileName = targetDataFarm
      ? DataFarmTypeUtil.toString(targetDataFarm as DataFarmType)
      : DataFarmTypeUtil.toString(this.dataFarmType) || 'Default';

    // normalize like "Subtask" (Capital S, rest lower)
    fileName = fileName.charAt(0).toUpperCase() + fileName.slice(1).toLowerCase();

    const path = `${basePath}/${transformerName} Icon ${fileName}.png`;

    //console.log('Transformer:', transformerName, 'TargetDataFarm:', fileName, '→ Path:', path);

    return path;
  }

  async addTransformer(transformerType: TransformType): Promise<boolean> {
    const dataFarmName = DataFarmTypeUtil.toString(this.dataFarmType);

      // if (!this.restricttransformers.find(x=>x.transformerid==transformerType).allowed) {
      //   this.snackBar.open(`Action not allowed for ${dataFarmName} Ingest Farm to add this Transformer`, 'Dismiss', {
      //     duration: 7500,
      //     panelClass: 'snackbar-error'
      //   });
      //   return ;
      // }
      
    //old
    // if (!this.viewModel.canAddTransformerToFarm(this.dataTransformConfigViewModel.ingestConfigs, this.dataFarmType) ) {
    //   this.snackBar.open(`Action not allowed.  Must have a ${dataFarmName} Ingest Farm to add a ${dataFarmName} Transformer`, 'Dismiss', {
    //     duration: 7500,
    //     panelClass: 'snackbar-error'
    //   });
    //   return ;
    // }

    ////console.log(`adding new transform of type: ${transformerType}`);
    //console.log("this.dataFarmType :",this.dataFarmType);
    var nodeID=this.userData.getSelectedNodeDetails().id;
    const index = this.viewModel.transforms.filter(tr=>tr.nodeID==nodeID).length + 1;
    const name = this.getTransformerDefaultName(transformerType);
    const items = this.viewModel.transforms;
    var nodeID=this.userData.getSelectedNodeDetails().id;
    const transform = TransformFactory.createInstance(transformerType, name, index,nodeID);
    transform.dataFarmType = this.dataFarmType;
    
    const transformVm = new TransformerViewModel(transform);
    this.unsavedTransformGuids.add(transformVm.guid);
    items.push(transformVm);

    // TODO: Find better way to keep in sync.
    // store in config transforms list also
    this.dataTransformConfigViewModel.transforms.push(transformVm);
    this.configViewModelService.setDataTransformConfigViewModel(this.dataTransformConfigViewModel);
    this.dataSourceBehaviorSubject.next(items);
    //this.stepModel.isComplete = true;
    //////console.log('transformer List add transformer = ', this.dataTransformConfigViewModel)
    const busyDialog = this.showBusyDialog('Adding transformer...');
    let saved = false;
    try {
      saved = await this.saveConfigImpl(history.state.nodename);
    } finally {
      busyDialog.close();
    }
    if (!saved) {
      // Never leave an unsaved transformer in the list: it could be opened, and nothing
      // on the server knows it.
      const rollBack = (list: TransformerViewModel[]) => {
        const i = list.indexOf(transformVm);
        if (i > -1) { list.splice(i, 1); }
      };
      rollBack(items);
      rollBack(this.dataTransformConfigViewModel.transforms);
      this.unsavedTransformGuids.delete(transformVm.guid);
      this.configViewModelService.setDataTransformConfigViewModel(this.dataTransformConfigViewModel);
      this.dataSourceBehaviorSubject.next(items);
      this.refreshLayout();
      return false;
    }
    await this.updateTransformerIsCached();
    await this.resetTransformer(transformVm.guid);
    this.sharedService.sendEvent("get_node_status_update");
    this.refreshLayout();
    return true;
  }

  async addTransformerColumnLookUp(transformerType: TransformType): Promise<void> {
    var attribute=await this.countNodeAttributes();
    if(attribute>0){
      this.addTransformer(transformerType);
    }else{
      this.displayInfo("Cannot add a Column Lookup Transformer as there are no attribution farms connected to the Processor Node.   Please add an attribution farm from the Refinery Menu.","Info");
    }
  }
  async countNodeAttributes(): Promise<number> {
    var nodedetails=this.userData.getSelectedNodeDetails();
    var attribute=nodedetails.sourcenodelink.filter(x=>x.linetype==='dotted').length;
    console.log("nodedetails :",nodedetails);
    console.log("attribute :",attribute);
    return attribute;
  }
  getTransformerDefaultName(transformerType: TransformType): string {
    switch (transformerType) {
      case TransformType.rowExtractor:
        return 'Row Extractor';
      case TransformType.activityNamer:
        return 'Activity Namer';
      case TransformType.eventGroupNamer:
        return 'Event Group Namer';
      case TransformType.eventNamer:
        return 'Event Namer';
      case TransformType.groupAttributor:
        return 'Group Attributor';
      case TransformType.columnCalc:
        return 'Column Calc';
        //return 'Column Calculator';
      case TransformType.columnLookup:
        return 'Column Lookup';
      case TransformType.farmConverter:
        return 'Farm Converter';
      case TransformType.farmJoiner:
        return 'Farm Joiner';
      case TransformType.rowFiller:
        return 'Row Filler';
      case TransformType.anonymizer:
        return 'Anonymizer';
      case TransformType.columnCleaner:
        return 'Column Cleaner';
      default:
        return 'Unknown';
    }
  }
  hideshowrow(transform:any):boolean{
    var currentNodeID=this.userData.getSelectedNodeDetails();
    return (transform.transform.nodeID!=currentNodeID.id);
  }

  async updateNodeStatus(selectedNode) {
    // const busyIndicator = this.showBusyDialog('Please wait...');
    var UpdateNodesStatus: UpdateNodesStatusRequest = new UpdateNodesStatusRequest(selectedNode.refineryConfigGuid, selectedNode.id, NodeRunStatus.PipelineModified )
    let updateStatusreturn = await this.configService.updateUpcomingNodesStatus(UpdateNodesStatus);
    // this.snackBar.open(updateStatusreturn.message.toString(), 'Dismiss', {
    //   duration: 5000
    // });
    // busyIndicator.close();
    // }
  } 
  // Save the config for transformers
  /** Saves the node's transformers. Saves run one at a time; the result says whether
   * this one landed. Opening a transformer waits for this chain (showTransformDetail). */
  saveConfigImpl(configName: string = history.state.nodename): Promise<boolean> {
    const pendingGuids = this.dataTransformConfigViewModel.transforms.map(t => t.guid);
    const result = this.saveChain.then(() => this.saveConfigOnce(configName)).then(ok => {
      if (ok) { pendingGuids.forEach(g => this.unsavedTransformGuids.delete(g)); }
      return ok;
    });
    this.saveChain = result.catch(() => false);
    return result;
  }

  private async saveConfigOnce(configName: string): Promise<boolean> {
    // const busyDialog = this.showBusyDialog('Saving...n');
    var currentDateTime =  this.datePipe.transform(new Date(), 'yyyy-MM-dd HH:mm:ss');

    try {

      const selectedNode = this.userData.getSelectedNodeDetails();

      const RefineryConfigJson = await this.configService.getRefineryConfigByGuidAsync(selectedNode.refineryConfigGuid);
      ////console.log('Refinery json in save = ', RefineryConfigJson) ;
      RefineryConfigJson.nodes.filter(newNode => newNode.id == selectedNode.id)[0].data.nodeStatus = NodeRunStatus.NotRun
      RefineryConfigJson.nodes.filter(newNode => newNode.id == selectedNode.id)[0].data.lastSaveddDateTime = currentDateTime

      let RefineryJson = await this.configService.UpsertRefineryConfig(RefineryConfigJson);



        
      // ////console.log('SAVE configviewModel in transformerlist = ', this.dataTransformConfigViewModel)
      //this.configviewModel.saveInProgress = true;

      let config = this.dataTransformConfigViewModel.dataTransformerConfig ;
      config.name = configName;
      config.organizationId = 1;


      // Save the transforms
      // config.transforms = this.dataTransformConfigViewModel.transforms 
      config.transforms = this.dataTransformConfigViewModel.transforms.map(t => t.transform);


      // Save the ingest transforms
      // this.DataConnectorforRun = config.dataConnectorMappings;
      config = await this.configService.upsertConfig(config);
      ////console.log('Biswa saved config details transformer-list = ', config);
      // this.allNewConfigAfterSave = config;



      this.configViewModelService.setDataTransformConfigViewModel(new DataTransformConfigViewModel(config));
      // Update the upcoming node's status
      // this.updateNodeStatus(selectedNode)

      // Save the configGuid in Refinery config
      // this.cdRef.detectChanges();
      // this.snackBar.open(`Successfully saved Node '${config.name}'`, 'Dismiss', {
      //   duration: 5000
      // });
      return true;
    } catch (err) {
      this.displayError(`Failed to save '${configName}'`, 'Error', err);
      return false;
    }
  }
  async updateTransformerIsCached():Promise<void> {
    const nodeID=this.userData.getNodeId();
    var request=new ResetNodeStatusRequest(this.userData.getRefineryId(),nodeID);
    var cacheStatusResponse=await this.configService.getTransformersNodeStatus(request);
    const cachedIds = cacheStatusResponse.transformerCacheStatus;
    const lastRunStatus = cacheStatusResponse.transformerLastRunStatus ?? {};
    this.viewModel.transforms.filter(x=>x.nodeID === nodeID).forEach(trans=>{
      trans.isCached = cachedIds.includes(trans.guid) ? 'Cached': 'Not Cached';
      trans.lastRunStatus = lastRunStatus[trans.guid]?.status ?? '';
      trans.lastRunMessage = lastRunStatus[trans.guid]?.message ?? '';
      trans.lastRunTime = lastRunStatus[trans.guid]?.runTime ?? '';
      trans.lastRunOutputRows = lastRunStatus[trans.guid]?.outputRows ?? null;
      trans.lastRunOutputCols = lastRunStatus[trans.guid]?.outputCols ?? null;
    });
    // MatTableDataSource only re-renders when its own .data is reassigned --
    // mutating trans properties in place (above) is invisible to it even with
    // detectChanges() called right after, the same way every other refresh in
    // this file (dropTable, etc.) already re-pushes through this subject. Without
    // this, a caller like the "reset_node_successfully"/"display_output_preview"
    // subscribers below shows stale Status/Cache State until an unrelated event
    // (e.g. a window resize) forces a full re-layout that happens to surface the
    // already-correct underlying data.
    this.dataSourceBehaviorSubject.next(this.viewModel.transforms);
  }
  async resetTransformer(transformerGuid:string):Promise<void> {
    // const translist=this.dataTransformConfigViewModel.transforms.filter(trans=>trans.nodeID===this.userData.getNodeId() && trans.index>=fromResetIndex);
    // const transformerGuids = translist.map(transformer => transformer.guid);
    try {
      console.log(`resetTransformer transformerGuid: '${transformerGuid}'`);
      var resetTransformerRequest = new ResetTransformerRequest(this.userData.getUserId(), 
      this.userData.getOrgId(),
      this.userData.getRefineryId(),
      this.userData.getNodeId(),
      transformerGuid);
      await this.configService.resetTransformer(resetTransformerRequest);
    } catch (err) {
      this.displayError(`Failed to Reseting transform`, 'Error', err);
    }
  }
}

class TransformFactory {
  static createInstance(transformType: TransformType, name: string, index: number,nodeID:string): DataTransform<any> {
    switch (transformType) {
      case TransformType.rowExtractor:
        return new RowExtractorTransform(0, index, name,nodeID);
      case TransformType.activityNamer:
        return new ActivityNamingTransform(0, index, name,nodeID);
      case TransformType.eventGroupNamer:
        return new EventGroupNamerTransform(0, index, name,nodeID);
      case TransformType.eventNamer:
        return new EventNamerTransform(0, index, name,nodeID);
      case TransformType.groupAttributor:
        return new GroupAttributorTransform(0, index, name,nodeID);
      case TransformType.columnCalc:
        return new ColumnCalcTransform(0, index, name,nodeID);
      case TransformType.columnLookup:
        return new ColumnLookupTransform(0, index, name,nodeID);
      case TransformType.farmConverter:
        return new FarmConverterTransform(0, index, name,nodeID);
      case TransformType.farmJoiner:
        return new JoinerTransform(0, index, name,nodeID);
      case TransformType.rowFiller:
        return new RowFillerTransform(0, index, name,nodeID);
      case TransformType.anonymizer:
        return new AnonymizerTransform(0, index, name,nodeID);
      case TransformType.columnCleaner:
        return new ColumnCleanerTransform(0, index, name,nodeID);
      default:
        return null;
    }
  }
getIconForType(type: DataDictionaryType): string {
  switch (type) {
    case DataDictionaryType.activity:
      return 'assets/images/activity-icon.png';
    case DataDictionaryType.case:
      return 'assets/images/case-icon.png';
    case DataDictionaryType.user:
      return 'assets/images/user-icon.png';
    case DataDictionaryType.subTask:
      return 'assets/images/Activity Namer Icon Subtask.png';
    case DataDictionaryType.task:
      return 'assets/images/Activity Namer Icon Task.png';
    case DataDictionaryType.behavior:
      return 'assets/images/behavior-icon.png';
    case DataDictionaryType.master:
      return 'assets/images/master-icon.png';
    default:
      return 'assets/images/default-icon.png';
  }
}



}
